import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

const CustomSelect = ({ value, options, onChange, minWidth = 'auto', icon }) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const selectedOption = options.find(o => o.value === value) || options[0];

    return (
        <div ref={containerRef} className="relative flex-1">
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                className="w-full h-8 px-2.5 flex items-center justify-between gap-1.5 bg-transparent border border-[var(--border)] rounded-md cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors"
                style={{ minWidth }}
            >
                <div className="flex items-center gap-1.5 overflow-hidden text-[13px] font-medium text-[var(--text-primary)]">
                    {icon && <span className="text-[var(--text-tertiary)] flex">{icon}</span>}
                    <span className="truncate">{selectedOption?.label}</span>
                </div>
                <ObliqueIcon name="expand_more" size={14} color="var(--text-tertiary)" />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-[calc(100%+4px)] left-0 min-w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-lg z-[2000] p-1 flex flex-col gap-0.5 max-h-[240px] overflow-y-auto custom-scrollbar"
                    >
                        {options.map(opt => (
                            <button
                                key={opt.value}
                                onClick={(e) => { e.stopPropagation(); onChange(opt.value); setOpen(false); }}
                                className={`w-full text-left px-2 py-1.5 border-none cursor-pointer rounded-md text-[13px] transition-colors flex items-center justify-between gap-2 ${opt.value === value ? 'bg-[var(--bg-subtle)] text-[var(--accent)] font-medium' : 'bg-transparent text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]'}`}
                            >
                                <span>{opt.label}</span>
                                {opt.value === value && (
                                    <ObliqueIcon name="check" size={14} color="var(--accent)" />
                                )}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const SortManager = ({ properties, onSortChange, currentSorts = [], showTrigger = true, open: externalOpen, onOpenToggle, iconOnly = false }) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
    const setIsOpen = onOpenToggle || setInternalOpen;
    const [sorts, setSorts] = useState(currentSorts);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        setSorts(currentSorts || []);
    }, [currentSorts]);

    const addSort = () => {
        const defaultProp = properties[0];
        const newSort = {
            id: Math.random().toString(36).substr(2, 9),
            propertyId: defaultProp.id,
            direction: 'desc'
        };
        const newSorts = [...sorts, newSort];
        setSorts(newSorts);
        onSortChange(newSorts);
    };

    const updateSort = (id, updates) => {
        const newSorts = sorts.map(s => s.id === id ? { ...s, ...updates } : s);
        setSorts(newSorts);
        onSortChange(newSorts);
    };

    const removeSort = (id) => {
        const newSorts = sorts.filter(s => s.id !== id);
        setSorts(newSorts);
        onSortChange(newSorts);
    };

    const propertyOptions = properties.map(p => ({ value: p.id, label: p.label }));
    const directionOptions = [
        { value: 'asc', label: 'Ascending' },
        { value: 'desc', label: 'Descending' }
    ];

    return (
        <div className="relative" ref={containerRef}>
            {showTrigger && (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`h-[30px] flex items-center justify-center gap-1.5 px-2 bg-transparent border border-[var(--border)] rounded-md cursor-pointer transition-colors hover:bg-[var(--bg-hover)] ${iconOnly ? 'w-[30px] px-0' : ''} ${sorts.length > 0 ? 'text-[var(--accent)] bg-[var(--accent-subtle)] border-transparent' : 'text-[var(--text-secondary)]'}`}
                >
                    <ObliqueIcon name="swap_vert" size={16} filled={sorts.length > 0} />
                    {!iconOnly && (
                        <span className="font-medium text-[13px]">{sorts.length > 0 ? `${sorts.length} sorts` : 'Sort'}</span>
                    )}
                    {sorts.length > 0 && <ObliqueIcon name="expand_more" size={14} />}
                </button>
            )}

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 400 }}
                        className="absolute top-[calc(100%+4px)] right-0 min-w-[300px] bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.1)] z-[1000] p-1.5 flex flex-col origin-top-right"
                    >
                        {sorts.length > 0 && (
                            <div className="flex flex-col gap-1 mb-1">
                                <AnimatePresence mode="popLayout">
                                    {sorts.map((sort, index) => (
                                        <motion.div
                                            key={sort.id}
                                            layout
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.95 }}
                                            transition={{ duration: 0.15 }}
                                            className="flex items-center gap-1 group p-1"
                                        >
                                            <div className="text-[var(--text-tertiary)] opacity-0 group-hover:opacity-100 transition-opacity cursor-grab flex items-center justify-center w-4 shrink-0">
                                                <ObliqueIcon name="drag_indicator" size={14} />
                                            </div>
                                            
                                            <div className="flex items-center gap-1.5 flex-1">
                                                <CustomSelect
                                                    value={sort.propertyId}
                                                    options={propertyOptions}
                                                    onChange={(val) => updateSort(sort.id, { propertyId: val })}
                                                    icon={<ObliqueIcon name="text_fields" size={14} />}
                                                />
                                                
                                                <CustomSelect
                                                    value={sort.direction}
                                                    options={directionOptions}
                                                    onChange={(val) => updateSort(sort.id, { direction: val })}
                                                />
                                            </div>

                                            <button
                                                onClick={() => removeSort(sort.id)}
                                                className="bg-transparent border-none text-[var(--text-tertiary)] hover:text-[var(--text-primary)] p-1 cursor-pointer shrink-0 flex items-center justify-center rounded-md"
                                                title="Remove sort"
                                            >
                                                <ObliqueIcon name="close" size={16} />
                                            </button>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </div>
                        )}

                        <div className="flex flex-col gap-0.5 mt-1">
                            <button
                                onClick={addSort}
                                className="w-full text-left px-2 py-1.5 bg-transparent border-none rounded-md text-[var(--text-secondary)] text-[13px] flex items-center gap-2 cursor-pointer transition-colors hover:bg-[var(--bg-subtle)]"
                            >
                                <ObliqueIcon name="add" size={16} color="var(--text-tertiary)" /> Add sort
                            </button>
                            
                            {sorts.length > 0 && (
                                <button
                                    onClick={() => { setSorts([]); onSortChange([]); }}
                                    className="w-full text-left px-2 py-1.5 bg-transparent border-none rounded-md text-[var(--text-secondary)] text-[13px] flex items-center gap-2 cursor-pointer transition-colors hover:bg-[var(--bg-subtle)]"
                                >
                                    <ObliqueIcon name="delete" size={16} color="var(--text-tertiary)" /> 
                                    {sorts.length === 1 ? 'Delete sort' : 'Delete sorts'}
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SortManager;
